import type { BuildingRules, LevelDef, Lift } from '@full-elevator/sim';
import {
  ARCHETYPES,
  BASE_LOADOUT,
  generateTraffic,
  PAYOUT_BY_STARS,
  stockSpec,
  TRAFFIC_PATTERNS,
  UPGRADE_SYSTEMS,
  V01_ELEVATOR,
} from '@full-elevator/sim';
import { z } from 'zod';
import { getBuilding, liftFor, rulesFor } from './buildings.js';

/**
 * The contract every level file must satisfy.
 *
 * Levels are data, not code — there is no per-level logic anywhere in the
 * codebase. This schema is what makes that safe: a malformed level fails loudly
 * at load with a field-level message instead of misbehaving at runtime.
 */

const floorIndex = z.number().int().min(0).max(63);

/** The crews that can be called out (ADR-0029): archetypes with a call-out. */
const CREWS = Object.keys(ARCHETYPES).filter((a) => ARCHETYPES[a]?.callout) as [
  string,
  ...string[],
];

/**
 * Who may be sprinkled through a crowd (`mix`): anyone who needs nothing
 * else to make sense. Not an emergency, which comes with its return; not a
 * window cleaner, who comes with a trip back; not a postman or a bellhop
 * without a round, nor a commuter without a train (ADR-0029, ADR-0031,
 * ADR-0043).
 */
const NEEDS_MORE = new Set([
  'postman',
  'bellhop',
  'commuter',
  'windowCleanerDown',
  // The hotel's departures (ADR-0051): nobody runs for a time that is not set.
  'shuttleGuest',
  'breakfastGuest',
  'newlywed',
]);

/** Who runs for a departure at a set time (ADR-0031, ADR-0051). */
const DEPARTING = ['commuter', 'shuttleGuest', 'breakfastGuest', 'newlywed'] as const;
const MIXABLE = Object.keys(ARCHETYPES).filter(
  (a) => !ARCHETYPES[a]?.critical && !ARCHETYPES[a]?.callout && !NEEDS_MORE.has(a),
) as [string, ...string[]];

/** A building's own people (ADR-0031): anyone but an emergency crew. */
const CAST = Object.keys(ARCHETYPES).filter((a) => !ARCHETYPES[a]?.critical) as [
  string,
  ...string[],
];

const spawnSchema = z.object({
  atMs: z.number().int().min(0),
  origin: floorIndex,
  destination: floorIndex,
  /** A named bundle of traits from ARCHETYPES. Omit for an ordinary passenger. */
  archetype: z.enum(Object.keys(ARCHETYPES) as [string, ...string[]]).optional(),
  /** Ties this spawn to others in the level; they board all-or-nothing. */
  group: z.string().min(1).optional(),
  /** Appears `atMs` after an earlier spawn, by its place in the list, is delivered. */
  follows: z.number().int().min(0).optional(),
  /** Floors to stop at on the way, before getting off: a postman's round (ADR-0031). */
  stops: z.array(floorIndex).max(6).optional(),
  /**
   * Per-spawn overrides on top of the archetype. Kept narrow on purpose: a level
   * leaning on these is describing an archetype that deserves a name.
   */
  traits: z
    .object({
      size: z.number().int().min(1).max(4).optional(),
      patienceMs: z.number().int().min(1_000).nullable().optional(),
      deadlineMs: z.number().int().min(1_000).nullable().optional(),
      vip: z.boolean().optional(),
    })
    .optional(),
});

const objectiveSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('deliverAll') }),
  z.object({ kind: z.literal('deliverN'), count: z.number().int().min(1) }),
  z.object({
    kind: z.literal('serviceAbove'),
    count: z.number().int().min(1),
    minService: z.number().int(),
  }),
  z.object({
    kind: z.literal('survive'),
    untilMs: z.number().int().min(1_000),
    maxLost: z.number().int().min(0),
  }),
  /** A puzzle shift (ADR-0037): everyone home within this many trips. */
  z.object({ kind: z.literal('withinTrips'), trips: z.number().int().min(1) }),
]);

/** A shift's bonus goal (ADR-0040): one thing beyond winning it. */
const bonusSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('noneLost') }),
  z.object({ kind: z.literal('couriersOnTime') }),
  z.object({ kind: z.literal('allVips') }),
  z.object({ kind: z.literal('withinTrips'), trips: z.number().int().min(1) }),
]);

/**
 * A shape of demand instead of a list of people.
 *
 * Expanded to a concrete spawn list here, at load, using the level's own seed —
 * so a generated level is a *designed* level that plays the same every time,
 * not a lottery. See `traffic.ts`.
 */
const trafficSchema = z.object({
  pattern: z.enum(TRAFFIC_PATTERNS as unknown as [string, ...string[]]),
  count: z.number().int().min(1).max(60),
  overMs: z.number().int().min(0).optional(),
  lobby: floorIndex.optional(),
  hub: floorIndex.optional(),
  boundary: floorIndex.optional(),
  mix: z
    .array(
      z.object({
        archetype: z.enum(MIXABLE),
        count: z.number().int().min(1),
      }),
    )
    .optional(),
  groupsOf: z.number().int().min(2).max(4).optional(),
  groups: z.number().int().min(1).optional(),
  /** Emergencies (ADR-0029): a crew called out, and their return written after them. */
  callouts: z
    .array(
      z.object({
        archetype: z.enum(CREWS),
        atMs: z.number().int().min(0),
        from: floorIndex.optional(),
        to: floorIndex.optional(),
      }),
    )
    .optional(),
  /** The building's own people (ADR-0031), each at a set time. */
  cast: z
    .array(
      z.object({
        archetype: z.enum(CAST),
        atMs: z.number().int().min(0),
        from: floorIndex.optional(),
        to: floorIndex.optional(),
        stops: z.array(floorIndex).max(6).optional(),
      }),
    )
    .optional(),
  /** Departures at a set time (ADR-0031, ADR-0051), and how many run for each. */
  trains: z
    .array(
      z.object({
        departsAtMs: z.number().int().min(25_000),
        riders: z.number().int().min(1).max(12),
        to: floorIndex.optional(),
        archetype: z.enum(DEPARTING).optional(),
        party: z.number().int().min(2).max(4).optional(),
      }),
    )
    .optional(),
  /** No two arrivals further apart than this (ADR-0038): quiet stretches get ordinary people. */
  maxGapMs: z.number().int().min(1000).optional(),
});

const loadoutSchema = z
  .object(
    Object.fromEntries(
      UPGRADE_SYSTEMS.map((system) => [system, z.number().int().min(0).max(8).optional()]),
    ) as Record<string, z.ZodOptional<z.ZodNumber>>,
  )
  .optional();

/**
 * Cabin overrides. v0.1 ships one elevator with fixed stats, so a level may only
 * nudge where the car starts; anything else would be progression, which belongs
 * to v0.3. Widen this deliberately, not by accident.
 */
const elevatorOverrideSchema = z
  .object({
    startFloor: floorIndex.optional(),
  })
  .optional();

export const levelSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  purpose: z.string().min(1),
  floors: z.number().int().min(2).max(64),
  /** Which building this level sits in. Absent means a plain tower with no structural rules. */
  building: z.string().min(1).optional(),
  elevator: elevatorOverrideSchema,
  /**
   * A second car in the same bank (ADR-0034), driven by the player like the
   * first, and where it starts. One at most: two shafts are what fit across a
   * phone.
   */
  extraCars: z
    .array(z.object({ startFloor: floorIndex }))
    .max(1)
    .optional(),
  /** Either an explicit cast list, or a traffic shape to generate one from. */
  spawns: z.array(spawnSchema).min(1).optional(),
  traffic: trafficSchema.optional(),
  /** Seeds traffic generation. A level with a fixed seed is a fixed level. */
  seed: z.number().int().min(1).optional(),
  parLoadout: loadoutSchema,
  starsFrom: z.enum(['humans', 'bots']).optional(),
  objective: objectiveSchema,
  timeLimitMs: z.number().int().min(1_000),
  stars: z.object({
    /** `time` grades low-to-high, `service` high-to-low. Defaults to `time`. */
    metric: z.enum(['time', 'service', 'trips']).optional(),
    three: z.number().int(),
    two: z.number().int(),
  }),
  expectsPressure: z.boolean().optional(),
  /** A hard shift (ADR-0037): announced, and paid half as much again. */
  hard: z.boolean().optional(),
  /** One goal beyond winning (ADR-0040), optional to the player. */
  bonus: bonusSchema.optional(),
  /** A puzzle's plan, floor by floor (ADR-0037), shown after a loss. `pnpm puzzles` writes it. */
  plan: z.array(z.number().int().min(0)).optional(),
});

export type RawLevel = z.input<typeof levelSchema>;

export class LevelValidationError extends Error {
  constructor(
    readonly levelId: string,
    readonly issues: string[],
  ) {
    super(`Level "${levelId}" is invalid:\n  - ${issues.join('\n  - ')}`);
    this.name = 'LevelValidationError';
  }
}

/**
 * Zod leaves absent optional keys present-and-undefined, which `exactOptionalPropertyTypes`
 * rejects. Dropping them keeps "the file did not say" distinct from "the file said undefined".
 */
type Defined<T> = { [K in keyof T]-?: Exclude<T[K], undefined> };

function stripUndefined<T extends object>(value: T): Partial<Defined<T>> {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as Partial<
    Defined<T>
  >;
}

/**
 * Parse one level file into a `LevelDef`.
 *
 * Checks the schema can't express: star thresholds that are ordered wrongly, a
 * time limit that beats its own targets, and floor references outside the
 * building. Everything reported at once, so a bad file takes one fix, not five.
 */
/** A trait of a spawn, from the spawn itself or its archetype. */
function traitOf<K extends 'patienceMs' | 'deadlineMs' | 'vip' | 'critical'>(
  spawn: { readonly archetype?: string | undefined; readonly traits?: object | undefined },
  key: K,
): unknown {
  const own = (spawn.traits as Record<string, unknown> | undefined)?.[key];
  return own ?? ARCHETYPES[spawn.archetype ?? 'normal']?.[key];
}

/**
 * A bonus goal must be about something in the shift, and something a run can
 * miss (ADR-0040): every VIP where there are none, or nobody lost where nobody
 * can be, is a medal for turning up.
 */
function bonusProblems(
  data: z.infer<typeof levelSchema>,
  spawns: readonly {
    readonly archetype?: string | undefined;
    readonly traits?: object | undefined;
  }[],
  building: BuildingRules,
): string[] {
  const bonus = data.bonus;
  if (!bonus) return [];
  const has = (key: 'patienceMs' | 'deadlineMs' | 'vip') =>
    spawns.some((s) => traitOf(s, key) != null && traitOf(s, key) !== false);
  const onTheClock = spawns.some(
    (s) => traitOf(s, 'deadlineMs') != null && traitOf(s, 'critical') !== true,
  );
  switch (bonus.kind) {
    case 'couriersOnTime':
      return onTheClock ? [] : ['bonus: every courier on time, in a shift with no courier'];
    case 'allVips':
      return has('vip') ? [] : ['bonus: every VIP, in a shift with no VIP'];
    case 'noneLost':
      return has('patienceMs') || has('deadlineMs') || building.landingCapacity !== undefined
        ? []
        : ['bonus: nobody lost, in a shift where nobody can be'];
    case 'withinTrips':
      return data.objective.kind === 'withinTrips'
        ? ['bonus: a trip limit on a shift already graded on trips']
        : [];
  }
}

export function parseLevel(raw: unknown): LevelDef {
  const result = levelSchema.safeParse(raw);
  const id = typeof raw === 'object' && raw !== null && 'id' in raw ? String(raw.id) : '<unknown>';

  if (!result.success) {
    throw new LevelValidationError(
      id,
      result.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }

  const data = result.data;
  const issues: string[] = [];
  const top = data.floors - 1;
  const metric = data.stars.metric ?? 'time';

  // A level says who turns up either by listing them or by naming a shape of
  // demand. Exactly one — both would be two sources of truth for the same thing.
  if (!data.spawns === !data.traffic) {
    throw new LevelValidationError(data.id, [
      data.spawns
        ? 'has both an explicit spawn list and a traffic pattern; use one or the other'
        : 'has neither a spawn list nor a traffic pattern, so nobody ever arrives',
    ]);
  }

  // Resolved before traffic is generated: a building with two entrances changes
  // where the crowd starts, which is a property of the demand rather than
  // something applied to it afterwards.
  let building: BuildingRules;
  let lift: Lift | undefined;
  let payout: Readonly<Record<number, number>> | undefined;
  try {
    building = rulesFor(data.building);
    lift = liftFor(data.building);
    payout = data.building === undefined ? undefined : getBuilding(data.building).payout;
    // A hard shift pays half as much again (ADR-0037), on its building's own table.
    if (data.hard) payout = hardPay(payout ?? PAYOUT_BY_STARS);
  } catch (error) {
    throw new LevelValidationError(data.id, [(error as Error).message]);
  }

  const spawns =
    data.spawns ??
    generateTraffic(
      data.traffic as Parameters<typeof generateTraffic>[0],
      data.floors,
      data.seed ?? 1,
      building,
    );

  if (spawns.length === 0) {
    throw new LevelValidationError(data.id, [
      `traffic pattern "${data.traffic?.pattern}" generated nobody in a ${data.floors}-floor building`,
    ]);
  }

  // Time grades low-to-high; service grades high-to-low. Getting this backwards
  // silently makes three stars unreachable, so it is checked rather than assumed.
  if (metric === 'time' && data.stars.three >= data.stars.two) {
    issues.push(
      `stars: three-star time (${data.stars.three}ms) must be faster than two-star (${data.stars.two}ms)`,
    );
  }
  if (metric === 'service' && data.stars.three <= data.stars.two) {
    issues.push(
      `stars: three-star score (${data.stars.three}) must be higher than two-star (${data.stars.two})`,
    );
  }
  // Trips grade low-to-high, like time, and two stars must fit the limit (ADR-0037).
  if (metric === 'trips' && data.stars.three > data.stars.two) {
    issues.push(
      `stars: three-star trips (${data.stars.three}) must be no more than two-star (${data.stars.two})`,
    );
  }
  if (metric === 'trips' && data.objective.kind !== 'withinTrips') {
    issues.push('stars: only a shift with a trip limit can be graded on trips');
  }
  if (
    data.objective.kind === 'withinTrips' &&
    (metric !== 'trips' || data.stars.two > data.objective.trips)
  ) {
    issues.push(
      `stars: a trip-limited shift grades on trips, with two stars within its ${data.objective.trips}`,
    );
  }
  if (metric === 'time' && data.stars.two > data.timeLimitMs) {
    issues.push(
      `stars.two (${data.stars.two}ms) is beyond timeLimitMs (${data.timeLimitMs}ms), so two stars is unreachable`,
    );
  }

  spawns.forEach((spawn, index) => {
    if (spawn.origin > top)
      issues.push(`spawns[${index}].origin ${spawn.origin} > top floor ${top}`);
    if (spawn.destination > top)
      issues.push(`spawns[${index}].destination ${spawn.destination} > top floor ${top}`);
    if (spawn.origin === spawn.destination)
      issues.push(`spawns[${index}] travels from floor ${spawn.origin} to itself`);
    if (spawn.atMs >= data.timeLimitMs)
      issues.push(`spawns[${index}] appears at ${spawn.atMs}ms, after the level's own time limit`);

    const patience =
      spawn.traits?.patienceMs ?? ARCHETYPES[spawn.archetype ?? 'normal']?.patienceMs;
    if (patience != null && spawn.atMs + patience > data.timeLimitMs) {
      issues.push(
        `spawns[${index}] runs out of patience at ${spawn.atMs + patience}ms, after the level's own time limit — they can never be lost, so the trait does nothing`,
      );
    }
    const deadline =
      spawn.traits?.deadlineMs ?? ARCHETYPES[spawn.archetype ?? 'normal']?.deadlineMs;
    if (deadline != null && deadline <= spawn.atMs) {
      issues.push(
        `spawns[${index}] has a deadline of ${deadline}ms but does not appear until ${spawn.atMs}ms, so it is already late on arrival`,
      );
    }
  });

  // Group members travel together, which only reads as one decision if they are
  // standing in the same place and going to the same place.
  const groups = new Map<string, { origin: number; destination: number; count: number }>();
  spawns.forEach((spawn, index) => {
    if (!spawn.group) return;
    const seen = groups.get(spawn.group);
    if (!seen) {
      groups.set(spawn.group, { origin: spawn.origin, destination: spawn.destination, count: 1 });
      return;
    }
    seen.count += 1;
    if (seen.origin !== spawn.origin || seen.destination !== spawn.destination) {
      issues.push(
        `spawns[${index}] is in group "${spawn.group}" but starts or ends somewhere different from the rest of the group`,
      );
    }
  });
  for (const [name, group] of groups) {
    if (group.count < 2) {
      issues.push(`group "${name}" has only one member, so travelling together means nothing`);
    }
  }

  const startFloor = data.elevator?.startFloor ?? V01_ELEVATOR.startFloor;
  if (startFloor > top) issues.push(`elevator.startFloor ${startFloor} > top floor ${top}`);
  data.extraCars?.forEach((car, index) => {
    if (car.startFloor > top)
      issues.push(`extraCars[${index}].startFloor ${car.startFloor} > top floor ${top}`);
  });

  const objective = data.objective;
  if (
    (objective.kind === 'deliverN' || objective.kind === 'serviceAbove') &&
    objective.count > spawns.length
  ) {
    issues.push(
      `objective needs ${objective.count} deliveries but only ${spawns.length} passengers spawn`,
    );
  }
  if (objective.kind === 'survive' && objective.untilMs > data.timeLimitMs) {
    issues.push(
      `the shift runs to ${objective.untilMs}ms but the level ends at ${data.timeLimitMs}ms, so it can never be survived`,
    );
  }

  issues.push(...bonusProblems(data, spawns, building), ...planProblems(data));

  if (issues.length > 0) throw new LevelValidationError(data.id, issues);

  return {
    id: data.id,
    name: data.name,
    purpose: data.purpose,
    floors: data.floors,
    // On the building's own stock car when it has one (ADR-0027). A level may
    // only move where the car starts, never its numbers, so a run recorded
    // before the building had a lift of its own can be put back on the
    // standard one exactly (`onStandardLift`).
    elevator: { ...V01_ELEVATOR, ...(lift ? stockSpec(lift.parts) : {}), startFloor },
    spawns: spawns.map((s) => ({
      atMs: s.atMs,
      origin: s.origin,
      destination: s.destination,
      ...(s.archetype === undefined ? {} : { archetype: s.archetype }),
      ...(s.group === undefined ? {} : { group: s.group }),
      ...(s.follows === undefined ? {} : { follows: s.follows }),
      ...(s.stops === undefined ? {} : { stops: s.stops }),
      ...(s.traits === undefined ? {} : { traits: stripUndefined(s.traits) }),
    })),
    objective: data.objective,
    timeLimitMs: data.timeLimitMs,
    ...(data.parLoadout === undefined
      ? {}
      : { parLoadout: { ...BASE_LOADOUT, ...stripUndefined(data.parLoadout) } }),
    stars: {
      three: data.stars.three,
      two: data.stars.two,
      ...(data.stars.metric === undefined ? {} : { metric: data.stars.metric }),
    },
    ...(Object.keys(building).length === 0 ? {} : { building }),
    ...(data.building === undefined ? {} : { buildingId: data.building }),
    ...(lift === undefined ? {} : { lift }),
    ...(payout === undefined ? {} : { payout }),
    ...(data.starsFrom === undefined ? {} : { starsFrom: data.starsFrom }),
    ...(data.expectsPressure === undefined ? {} : { expectsPressure: data.expectsPressure }),
    ...(data.extraCars === undefined ? {} : { extraCars: data.extraCars }),
    ...(data.plan === undefined ? {} : { plan: data.plan }),
    ...(data.hard ? { hard: true } : {}),
    ...(data.bonus === undefined ? {} : { bonus: data.bonus }),
  };
}

/** A plan is a puzzle's (ADR-0037), and every floor in it is in the building. */
function planProblems(data: z.output<typeof levelSchema>): string[] {
  if (data.plan === undefined) return [];
  const out: string[] = [];
  if (data.objective.kind !== 'withinTrips') out.push('plan: only a puzzle has a plan to show');
  if (data.plan.some((floor) => floor >= data.floors)) {
    out.push(`plan: a floor past the top of a ${data.floors}-floor building`);
  }
  return out;
}

/** Half as much again for every star count, in whole coins. */
function hardPay(table: Readonly<Record<number, number>>): Readonly<Record<number, number>> {
  return Object.fromEntries(
    Object.entries(table).map(([stars, coins]) => [stars, Math.round(coins * 1.5)]),
  );
}
